import { IAppState, IEngineMissingReason, ILog, Log } from "../../Types"
import { Icons } from "../../Components/Icon"
import { UserAdviceLevel, UserAdviceRole } from "../../Components/UserAdvice/Params"
import { FocusViewState } from "../FocusView"
import { UserAdvice, UserAdviceInstance, UserAdviceLocation, UserAdviceState } from "./Advice"
import { OverlayViewDefinition } from "../OverlayView"
import { userAdviceEngineMissingOverlay } from "./EngineMissingView"

// Stable `provisionalKey` of the engine-missing hint (#1227), mirroring Swift's
// `LogMetadata.engineMissingProvisionalKey`. Same reasoning as
// `SPEED_CHANGE_STOP_PROVISIONAL_KEY` in `StopAdvice`: a Swift `static let` has no
// generated counterpart, and this is only ever used as a map key.
//
// Deliberately absent from `provisionalCancelKey`'s allow-list (see
// `Views/Log/ProvisionalCancel.tsx`): this ghost is *answered*, never silently
// discarded, so it carries no cancel affordance.
export const ENGINE_MISSING_PROVISIONAL_KEY = "engineMissing";

// The "you're underway with no engine recorded" hint (#1227).
//
// This class performs **no detection whatsoever**. The primary app instance is the
// single decision point — several clients can be watching the same day (the skipper's
// phone, the chart-table tablet), and two of them deciding independently would race each
// other into a contradictory logbook — so it evaluates `EngineMissingConsumer` every
// navigation tick and broadcasts the verdict as a provisional ghost log. This advice is
// active exactly while that ghost is present, and every field it exposes is read
// straight off the ghost's metadata rather than derived here.
//
// The same arrangement handles the answer: whichever device answers, the primary writes
// the record and stops broadcasting, so the button disappears on *every* device on the
// next tick — including one that had the dialog open, whose `state.selected` then
// matches no advice instance.
export class EngineMissingAdvice extends UserAdvice {
  readonly role = UserAdviceRole.action;
  active: boolean = false;
  // The trigger log a confirmed engine start is back-dated to (the departure, the
  // anchor-up, or the motor vessel's first log under way).
  sinceLogId?: number = undefined;
  // That log's time, so the dialog can offer "since <time>" as the default anchor.
  since?: number = undefined;
  reason?: IEngineMissingReason = undefined;
  static level: number = 50;

  update(_app: IAppState, state: FocusViewState, currentLog?: ILog) {
    this.active = false;
    this.sinceLogId = undefined;
    this.since = undefined;
    this.reason = undefined;
    if (!currentLog) {
      return;
    }
    const hint = state.provisionalLogs?.[ENGINE_MISSING_PROVISIONAL_KEY];
    if (!hint || hint.dayId != currentLog.dayId) {
      return;
    }
    const entry = Log.parse(hint).dataParsed.engineMissing;
    if (!entry) {
      return;
    }
    this.sinceLogId = entry.sinceLogId;
    this.since = hint.time;
    this.reason = entry.reason;
    this.active = true;
  }

  getInstances(app: IAppState, location: UserAdviceLocation, currentLog?: ILog): UserAdviceInstance[] {
    // The hint belongs to the live current log, where the crew is actually working --
    // not to the day summary.
    if (!this.active || !currentLog || location != UserAdviceLocation.log) {
      return [];
    }
    const t = app.getT("UserAdviceEngineMissingView");
    return [
      new UserAdviceInstance(ENGINE_MISSING_PROVISIONAL_KEY, EngineMissingAdvice.level, { icon: Icons.MotorVessel, level: UserAdviceLevel.notice, shortTitle: t("title_short"), title: t(`title_${this.reason}`), type: "engineMissing" }, this, 0, location),
    ];
  }

  overlay(_app: IAppState, advice: UserAdviceInstance, state: UserAdviceState): OverlayViewDefinition | undefined {
    return userAdviceEngineMissingOverlay({ advice, state });
  }
}
